import React, { useState, useEffect, useRef } from 'react';
import { Card, Tag, Button, Modal, Input, message, Flex, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, WarningOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const MERCADO_PAGO_ACTIVATION_URL = process.env.REACT_APP_MERCADO_PAGO_ACTIVATION_URL;
const DISCONNECT_CONFIRM_COUNTDOWN_SECONDS = 15;

const MercadoPagoConnectionCard = ({ providerStatus, onStatusChange }) => {
  const [prerequisiteModalVisible, setPrerequisiteModalVisible] = useState(false);
  const [stepsModalVisible, setStepsModalVisible] = useState(false);
  const [step, setStep] = useState(1);
  const [pinCode, setPinCode] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [identification, setIdentification] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectModalVisible, setDisconnectModalVisible] = useState(false);
  const [disconnectSummaryLoading, setDisconnectSummaryLoading] = useState(false);
  const [disconnectSummary, setDisconnectSummary] = useState(null);
  const [disconnectCountdown, setDisconnectCountdown] = useState(DISCONNECT_CONFIRM_COUNTDOWN_SECONDS);
  const disconnectIntervalRef = useRef(null);

  const chargeActive = providerStatus?.charge_active === true;
  // Token do Mercado Pago expirou: o time ainda está marcado como vinculado,
  // mas as cobranças não funcionam até desvincular e vincular novamente.
  const connectionExpired = chargeActive && providerStatus?.status === 'EXPIRED';

  const handleStartActivation = () => {
    setPrerequisiteModalVisible(false);
    setStep(1);
    setPinCode('');
    setStepsModalVisible(true);
  };

  const handleCloseSteps = () => {
    setStepsModalVisible(false);
    setStep(1);
    setPinCode('');
    setIdentification(null);
  };

  const handleConfirmPin = async () => {
    if (!pinCode?.trim()) {
      message.error('Digite o código de 6 números para continuar');
      return;
    }

    try {
      setPinLoading(true);
      const response = await api.post('/organizer-charges/provider-activation/validate-pin', {
        code: pinCode
      });

      if (response?.data?.activation) {
        setIdentification(response.data.activation);
        setStep(4);
      }
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || 'Erro ao validar código de integração');
    } finally {
      setPinLoading(false);
    }
  };

  const handleConfirmIdentification = async () => {
    try {
      setPinLoading(true);
      await api.post('/organizer-charges/provider-activation/confirm', {
        code: pinCode
      });

      message.success('Integração confirmada com sucesso');
      handleCloseSteps();
      if (onStatusChange) onStatusChange();
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || 'Erro ao confirmar integração');
    } finally {
      setPinLoading(false);
    }
  };

  const handleCancelIdentification = () => {
    setIdentification(null);
    handleCloseSteps();
  };

  useEffect(() => {
    return () => {
      if (disconnectIntervalRef.current) {
        clearInterval(disconnectIntervalRef.current);
      }
    };
  }, []);

  const closeDisconnectModal = () => {
    if (disconnectIntervalRef.current) {
      clearInterval(disconnectIntervalRef.current);
      disconnectIntervalRef.current = null;
    }
    setDisconnectModalVisible(false);
    setDisconnectSummary(null);
    setDisconnectCountdown(DISCONNECT_CONFIRM_COUNTDOWN_SECONDS);
  };

  const handleDisconnect = async () => {
    try {
      setDisconnectSummaryLoading(true);
      const response = await api.get('/organizer-charges/provider-activation/disconnect-summary');
      setDisconnectSummary(response?.data || null);
      setDisconnectCountdown(DISCONNECT_CONFIRM_COUNTDOWN_SECONDS);
      setDisconnectModalVisible(true);

      disconnectIntervalRef.current = setInterval(() => {
        setDisconnectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(disconnectIntervalRef.current);
            disconnectIntervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || 'Erro ao buscar resumo de desvinculação');
    } finally {
      setDisconnectSummaryLoading(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    try {
      setDisconnecting(true);
      const response = await api.put('/organizer-charges/provider-activation/disconnect');
      const { charges_canceled, invoices_canceled, settings_canceled } = response?.data || {};
      message.success(
        `Conta do Mercado Pago desvinculada com sucesso. Canceladas: ${charges_canceled ?? 0} cobrança(s), ${invoices_canceled ?? 0} fatura(s) e ${settings_canceled ?? 0} configuração(ões).`
      );
      closeDisconnectModal();
      if (onStatusChange) onStatusChange();
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || 'Erro ao desvincular conta do Mercado Pago');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex align="center" justify="space-between" gap="1rem" wrap>
          <Flex align="center" gap="0.6rem">
            <span>Conta Mercado Pago:</span>
            {connectionExpired ? (
              <Tag icon={<WarningOutlined />} color="orange">Conexão expirada</Tag>
            ) : chargeActive ? (
              <Tag icon={<CheckCircleOutlined />} color="green">Vinculada</Tag>
            ) : (
              <Tag icon={<CloseCircleOutlined />} color="red">Não vinculada</Tag>
            )}
          </Flex>

          {chargeActive ? (
            <Button
              danger
              icon={<DisconnectOutlined />}
              loading={disconnectSummaryLoading}
              onClick={handleDisconnect}
            >
              Desvincular Conta
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={() => setPrerequisiteModalVisible(true)}
            >
              Vincular Conta Mercado Pago
            </Button>
          )}
        </Flex>
        {connectionExpired && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="A conexão com o Mercado Pago expirou"
            description="As cobranças não estão sendo geradas nem recebidas. Para voltar a cobrar, desvincule a conta e vincule novamente (pode ser a mesma conta do Mercado Pago)."
          />
        )}
      </Card>

      <Modal
        title="Etapa obrigatória para ativar a Gestão de Cobrança"
        open={prerequisiteModalVisible}
        onCancel={() => setPrerequisiteModalVisible(false)}
        footer={[
          <Button key="close-prerequisite" type="primary" onClick={handleStartActivation}>
            Entendi
          </Button>
        ]}
      >
        <div style={{ whiteSpace: 'pre-line' }}>
          {`Para começar a usar a cobrança automática no FuteZap, será necessário vincular uma conta do Mercado Pago.
            Essa conta deve estar no nome do organizador ou de alguém com acesso direto, pois será usada para receber os pagamentos via Pix.

            Importante:

            - Todo valor recebido é repassado 100% direto para sua conta Mercado Pago.
            - A responsabilidade sobre o que está sendo cobrado é exclusiva do organizador.`}
        </div>
      </Modal>

      <Modal
        title={`Ativação da Cobrança ${step > 1 ? ` - Passo ${step - 1}` : ''}`}
        open={stepsModalVisible}
        onCancel={handleCloseSteps}
        footer={[
          step > 1 && step < 4 && (
            <Button
              key="back-step"
              onClick={() => setStep((prev) => Math.max(1, prev - 1))}
            >
              Voltar
            </Button>
          ),
          step < 3 ? (
            <Button
              key="next-step"
              type="primary"
              onClick={() => setStep((prev) => Math.min(3, prev + 1))}
            >
              Próximo
            </Button>
          ) : step === 3 ? (
            <Button
              key="confirm-step-3"
              type="primary"
              loading={pinLoading}
              onClick={handleConfirmPin}
            >
              Confirmar código
            </Button>
          ) : (
            <>
              <Button
                key="confirm-data"
                type="primary"
                loading={pinLoading}
                onClick={handleConfirmIdentification}
              >
                Confirmar (tudo correto)
              </Button>
              <Button
                key="cancel-data"
                danger
                onClick={handleCancelIdentification}
              >
                Desistir (dados incorretos)
              </Button>
            </>
          )
        ].filter(Boolean)}
      >
        {step === 1 && (
          <div style={{ whiteSpace: 'pre-line' }}>
            {`🔧 Como ativar a cobrança no FuteZap (3 passos simples):

            1️⃣ Criar uma conta no Mercado Pago (caso ainda não tenha)
            2️⃣ Fazer a integração com o FuteZap (iremos te guiar no processo)
            3️⃣ Digitar o código de ativação gerado após a ativação no mercado pago

            🧾 Em cada cobrança emitida, será adicionada uma taxa de transação de R$0,99 + 1% referente a taxa do Mercado Pago, já embutida no valor final e paga pelo jogador.

            O sistema enviará até 3 lembretes automáticos para lembrar sobre o pagamento.

            Pronto! Depois disso, sua cobrança estará ativa e funcionando direto no WhatsApp ⚙️💸`}
          </div>
        )}

        {step === 2 && (
          <div style={{ whiteSpace: 'pre-line' }}>
            <div>{`Após abrir a sua conta no Mercado Pago e estar com os dados do acesso em mãos, clique no botão abaixo para fazer a ativação.`}</div>
            <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 16 }}>
              <Button
                type="primary"
                href={MERCADO_PAGO_ACTIVATION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Fazer a Ativação no Mercado Pago
              </Button>
            </div>
            <div>{`ATENÇÃO: Não solicitamos seus dados de acesso em nenhum momento, a configuração será sempre realizada diretamente no Mercado Pago.`}</div>
          </div>
        )}

        {step === 3 && (
          <div style={{ whiteSpace: 'pre-line' }}>
            <div>{`🔐 Confirmação de Integração

              Digite o código de 6 números que foi exibido na tela após a autorização com o Mercado Pago.

              Esse código é necessário para concluir a ativação da cobrança automática no FuteZap.

              Aguardando sua confirmação. ⏳`}
            </div>
            <Input
              style={{ marginTop: 16 }}
              placeholder="Digite o código de 6 números"
              maxLength={6}
              value={pinCode}
              onChange={(event) => setPinCode(event.target.value)}
            />
          </div>
        )}

        {step === 4 && identification && (
          <div style={{ whiteSpace: 'pre-line' }}>
            {`Identificamos o seu cadastro, verifique as informações a seguir:

            Nome: ${identification.customer_name}
            Documento: ${identification.customer_document_type} ${identification.document_number}`}
          </div>
        )}
      </Modal>

      <Modal
        title="Desvincular conta do Mercado Pago"
        open={disconnectModalVisible}
        onCancel={closeDisconnectModal}
        footer={[
          <Button key="back-disconnect" onClick={closeDisconnectModal}>
            Voltar
          </Button>,
          <Button
            key="confirm-disconnect"
            danger
            type="primary"
            loading={disconnecting}
            disabled={disconnectCountdown > 0}
            onClick={handleConfirmDisconnect}
          >
            {disconnectCountdown > 0 ? `Aguarde (${disconnectCountdown}s) para confirmar a desvinculação` : 'Sim, desvincular definitivamente'}
          </Button>
        ]}
      >
        <div>
          <p>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            Esta ação é <strong>definitiva e não pode ser desfeita</strong>. Ao desvincular a conta do Mercado Pago, serão <strong>canceladas imediatamente</strong>:
          </p>
          <ul>
            <li><strong>{disconnectSummary?.charges_count ?? 0} cobrança(s)</strong> em aberto (pendentes ou processadas)</li>
            <li><strong>{disconnectSummary?.invoices_count ?? 0} fatura(s)</strong> pendentes de pagamento pelos jogadores</li>
            <li><strong>{disconnectSummary?.settings_count ?? 0} configuração(ões)</strong> de cobrança (ativas, processadas ou inativas)</li>
          </ul>
          <p>
            Faturas já <strong>pagas pelo Mercado Pago</strong> ou com <strong>baixa manual confirmada</strong> não serão alteradas.
          </p>
          <p>
            Depois de desvincular, a geração de novas cobranças ficará bloqueada até vincular uma nova conta. Se você reconectar outra conta do Mercado Pago depois, nenhuma cobrança, fatura ou configuração antiga poderá ser reativada.
          </p>
          <p>Tem certeza que deseja continuar?</p>
        </div>
      </Modal>
    </>
  );
};

export default MercadoPagoConnectionCard;
